import { reportQueryDrop } from '@/lib/query-drops'
import type { TreeNode } from '../schemas/tree-node'
import type { FilterEntityFields } from '../schemas/filter-entity'
import { isGroupNode } from '../schemas/tree-node'
import { isRangeValue, isTimeSliceValue } from '../schemas/filter-entity'
import {
  validateColumnExists,
  validateCrossType,
  validateOperatorForColumn,
  validateValueType,
} from '../utils/validate-filter-entity'
import { resolveTimeSlice } from '../utils/resolve-time-slice'
import { ensureStickyFilters, type StickyColumnConfig } from '../utils/sticky-filters'
import { filtersToTreeState } from './profile-to-form'
import type { ColumnCatalogDef } from '@/types/column-catalog'
import { toHhMmSs } from '@/components/ui/time-input-utils'
import type {
  QueryFilterGroup,
  QueryFilterTree,
  QueryFilterNode,
  ScannerFilter,
} from '@/services/api/scanner-types'

interface TreeState {
  nodes: Record<string, TreeNode>
  entities: Record<string, FilterEntityFields>
  rootId: string | null
}

/**
 * Convert STORED profile filters (any supported stored shape — the profile
 * format `{column, operator, value}`, legacy flat arrays, or an already
 * query-shaped `{AND: [...]}`) into the canonical backend query shape
 * (`left`/`right` binary args).
 *
 * Use this whenever applying a saved profile/preset DIRECTLY to page filters
 * (e.g. the Filters ▾ quick-switch), bypassing the filter form. The form path
 * gets this translation for free (profileToForm → profileToQuery); a direct
 * apply that skips it stores profile-shaped leaves that request builders can't
 * read (`transformFiltersForBackend` throws on the missing `right`).
 */
export function storedFiltersToQuery(
  filters: unknown,
  columnMap: Record<string, ColumnCatalogDef> = {},
): QueryFilterGroup {
  return profileToQuery(
    filtersToTreeState(filters as Parameters<typeof filtersToTreeState>[0]),
    columnMap,
  )
}

/**
 * `storedFiltersToQuery` + sticky-column injection — the translation for
 * pages with sticky filter columns (e.g. history's `day0_date` scope).
 *
 * The filter sheet injects sticky rows into its form tree before producing
 * query filters; any DIRECT profile apply must do the same or the sticky
 * scope silently drops from the page (the history header's quick-switch and
 * the Recent Gappers seed both hit this). Sticky columns always take their
 * configured DEFAULTS — applying a profile resets page scope to the
 * profile's canonical view — and sticky leaves stored inside the profile
 * itself are replaced, mirroring the sheet's load behavior.
 */
export function storedFiltersToQueryWithSticky(
  filters: unknown,
  columnMap: Record<string, ColumnCatalogDef>,
  stickyConfigs: StickyColumnConfig[],
): QueryFilterGroup {
  const tree = filtersToTreeState(filters as Parameters<typeof filtersToTreeState>[0])
  if (tree.rootId && stickyConfigs.length > 0) {
    ensureStickyFilters(tree as typeof tree & { rootId: string }, stickyConfigs)
  }
  return profileToQuery(tree, columnMap)
}

/**
 * Convert form state into the nested scanner /v2 filter tree
 * that gets sent in the API request body.
 *
 * Produces `{ AND: [...] }` or `{ OR: [...] }` with recursive nesting,
 * matching the edge backend format with `left`/`right` binary args.
 */
export function profileToQuery(
  state: TreeState,
  columnMap: Record<string, ColumnCatalogDef> = {},
): QueryFilterGroup {
  if (!state.rootId) return { AND: [] }

  const root = state.nodes[state.rootId]
  if (!root || !('children' in root)) return { AND: [] }

  // ROOT wraps a single AND/OR group — unwrap it
  const topGroupId = root.children[0]
  if (!topGroupId) return { AND: [] }

  return buildGroup(topGroupId, state, columnMap)
}

function buildGroup(
  nodeId: string,
  state: TreeState,
  columnMap: Record<string, ColumnCatalogDef>,
): QueryFilterGroup {
  const node = state.nodes[nodeId]
  if (!node || !('children' in node)) return { AND: [] }

  const groupKey = node.type === 'OR' ? 'OR' : 'AND'
  const children: QueryFilterTree[] = []

  for (const childId of node.children) {
    const child = state.nodes[childId]
    if (!child) continue

    if (isGroupNode(child.type) && 'children' in child) {
      // A group with nothing to send (every row hidden, blank, or dropped) is left
      // out: SQLAlchemy drops an empty or_() today but deprecates it. Only nested
      // groups are pruned; the top level stays `{ AND: [] }`, a valid no-filter scan.
      const group = buildGroup(childId, state, columnMap)
      if ((group.AND ?? group.OR ?? []).length > 0) children.push(group)
    } else {
      const filter = buildLeafFilter(childId, state, columnMap)
      if (filter) children.push(filter)
    }
  }

  return { [groupKey]: children }
}

/**
 * Time-of-day columns store `HH:mm` (see `TimeInput`); the backend's TIME
 * columns need `HH:mm:ss`. Catalog-driven (`columnType: 'string_time'`) with
 * the catalog's own suffix rule as the fallback for callers that pass a
 * catalog lacking the column (a scanner map applied to a history filter).
 */
function isTimeOfDayColumn(key: string, def: ColumnCatalogDef | undefined): boolean {
  if (def) return def.columnType === 'string_time'
  return key.endsWith('_time') || key.endsWith('_halt')
}

function drop(entity: FilterEntityFields, reason: string): null {
  reportQueryDrop({ kind: 'filter', column: entity.left.column, reason })
  return null
}

function buildLeafFilter(
  nodeId: string,
  state: TreeState,
  columnMap: Record<string, ColumnCatalogDef>,
): QueryFilterNode | null {
  const entity = state.entities[nodeId]
  if (!entity || !entity.enabled || !entity.left.column) return null

  // Drop leaves whose LHS column no longer exists in the catalog (schema
  // drift). No-op when the catalog is empty (legacy call paths).
  if (validateColumnExists(entity, columnMap) !== null) return drop(entity, 'unknown-column')

  // Drop cross-type column comparisons (e.g. string LHS vs currency RHS).
  if (validateCrossType(entity, columnMap) !== null) return drop(entity, 'cross-type')

  // Drop leaves where the operator isn't valid for the LHS column's dataType
  // (e.g. ticker [string] + gt). Same silent-drop policy as cross-type above.
  if (validateOperatorForColumn(entity, columnMap) !== null) return drop(entity, 'operator')

  // Drop leaves whose RHS value type doesn't match the LHS column class
  // (e.g. numeric column with a string value). Primary defense is CI
  // drift-detection; this is the runtime safety net for preset-load +
  // hand-edited localStorage.
  if (validateValueType(entity, columnMap) !== null) return drop(entity, 'value-type')

  const right = entityRightToQuery(entity, {
    timeOfDay: isTimeOfDayColumn(entity.left.column, columnMap[entity.left.column]),
  })
  if (right === null) return drop(entity, 'no-right')

  // "Include rows with no value" only means something on columns the catalog
  // flags `allowNull` (share structure). A stored flag on any other column
  // (legacy-migrated profiles carried it on price and dollar-volume leaves)
  // is dropped here so the query matches what the row can show (Chris,
  // 2026-09-13). Only an empty catalog (legacy call paths) reaches here with
  // an unknown column; it can't be judged, so the flag passes through.
  const leftDef = columnMap[entity.left.column]
  const keepNull = entity.allowNull === true && (leftDef == null || leftDef.allowNull === true)

  return {
    left: { column: entity.left.column },
    operator: entity.operator,
    right,
    ...(keepNull ? { allowNull: true } : {}),
  }
}

/**
 * The backend binary argument for a form condition's right-hand side. Shared by filters and
 * aggregate conditions so a `between` range or a time slice compiles the same way on both paths.
 * Returns null when the condition cannot be sent (no value, or a slice with no active window).
 * `timeOfDay` appends seconds to clock values (`HH:mm` -> `HH:mm:ss`) for the backend's TIME columns.
 */
export function entityRightToQuery(
  entity: Pick<FilterEntityFields, 'operator' | 'right' | 'rightType'>,
  options: { timeOfDay?: boolean } = {},
): QueryFilterNode['right'] | null {
  const right = rawRight(entity)
  if (right === null || !options.timeOfDay || !('value' in right)) return right
  const v = right.value
  return { value: Array.isArray(v) ? v.map(toHhMmSs) : toHhMmSs(v) }
}

function rawRight(
  entity: Pick<FilterEntityFields, 'operator' | 'right' | 'rightType'>,
): QueryFilterNode['right'] | null {
  if (entity.operator === 'is_true') return { value: true }
  if (entity.operator === 'is_false') return { value: false }
  if (entity.right === null) return null
  if (entity.rightType === 'column' && typeof entity.right === 'string') {
    return { column: entity.right }
  }
  if (isTimeSliceValue(entity.right)) {
    // Resolve the slice array to a single scalar by picking the slice whose
    // window contains the current exchange-tz clock. Backend only ever sees
    // a plain filter with a scalar RHS — same shape any other `value` filter
    // produces. Mirrors legacy's `resolveSliceGroup` pattern.
    const resolved = resolveTimeSlice(entity.right)
    return resolved === null ? null : { value: resolved }
  }
  if (isRangeValue(entity.right)) {
    // A scalar operator can carry a range (the date picker always emits one, and
    // profiles were saved that way); `EQ [from, to]` is a 400, so send the start.
    return SCALAR_OPERATORS.has(entity.operator)
      ? { value: entity.right.low }
      : { value: [entity.right.low, entity.right.high] }
  }
  return { value: entity.right }
}

const SCALAR_OPERATORS: ReadonlySet<string> = new Set(['gt', 'gte', 'lt', 'lte', 'eq', 'neq'])

/**
 * Legacy flat converter — collects all leaf filters into a flat array.
 * Used by code that still needs `ScannerFilter[]` (e.g. backward compat).
 */
export function profileToFlatFilters(state: TreeState): ScannerFilter[] {
  if (!state.rootId) return []

  const root = state.nodes[state.rootId]
  if (!root || !('children' in root)) return []

  const filters: ScannerFilter[] = []
  collectFilters(root.children, state, filters)
  return filters
}

function collectFilters(childIds: string[], state: TreeState, out: ScannerFilter[]): void {
  for (const id of childIds) {
    const node = state.nodes[id]
    if (!node) continue

    if (isGroupNode(node.type) && 'children' in node) {
      collectFilters(node.children, state, out)
      continue
    }

    const entity = state.entities[id]
    if (!entity || !entity.enabled || !entity.left.column) continue

    if (entity.operator === 'is_true' || entity.operator === 'is_false') {
      out.push({
        column: entity.left.column,
        operator: entity.operator,
        value: entity.operator === 'is_true',
      })
      continue
    }

    if (entity.right === null) continue

    if (isTimeSliceValue(entity.right)) {
      out.push({
        column: entity.left.column,
        operator: entity.operator,
        value: entity.right,
      })
    } else if (isRangeValue(entity.right)) {
      out.push({
        column: entity.left.column,
        operator: entity.operator,
        value: [entity.right.low, entity.right.high],
      })
    } else {
      out.push({
        column: entity.left.column,
        operator: entity.operator,
        value: entity.right,
      })
    }
  }
}
